import numeral from 'numeral'
import { toImmutable } from '@modugen/ui-toolkit'
import {
  loadPointOnDomain,
  magnitudeArrowDirection,
  scaleVector,
  localToGlobalForce,
} from '../utils'

interface LoadDisplayInputData {
  domainStart: Point
  domainEnd: Point
  scale: number
  domain: Domain
  labelFormat?: LoadSceneLabelFormatOptions
}
interface LineLoadDisplayInputData extends LoadDisplayInputData {
  load: LineLoad
}

interface PointLoadDisplayInputData extends LoadDisplayInputData {
  load: PointLoad
}

export const scaleLineLoadForce = (scale: number, magnitudeScaled: number) => {
  // return converted data in kilo value and in two decimal places
  const scaledForce = scale < 1.0e-3 ? 0 : magnitudeScaled * scale
  return scaledForce
}

export type LoadSceneLabel = string | string[]

export interface LoadSceneLabelFormatOptions {
  showUnitsInScene?: boolean
  showUnitInNewLine?: boolean
}

const formatLoadForceValue = (value: number) => {
  let formatter = '0[.][00]a'
  if (Math.abs(value) >= 0 && Math.abs(value) < 1000) {
    formatter = '0a' // no decimal places for values below 1k
  }
  return numeral(value).format(formatter)
}

const NUMERAL_ABBREVIATION_PATTERN = /^(-?[\d.,]+)([kmbt])$/i

export const mapNumeralAbbreviationToUnitPrefix = (abbreviation: string): string => {
  switch (abbreviation.toLowerCase()) {
    case 'k':
      return 'k'
    case 'm':
      return 'M'
    case 'b':
      return 'G'
    case 't':
      return 'T'
    default:
      return abbreviation
  }
}

export const splitLoadForceValueAndPrefix = (
  value: number,
): { valueText: string; unitPrefix: string } => {
  const formatted = formatLoadForceValue(value)
  const match = formatted.match(NUMERAL_ABBREVIATION_PATTERN)

  if (!match) {
    return { valueText: formatted, unitPrefix: '' }
  }

  return {
    valueText: match[1],
    unitPrefix: mapNumeralAbbreviationToUnitPrefix(match[2]),
  }
}

const formatLoadForceSceneLabel = (
  value: number,
  baseUnit: 'N' | 'N/m' | 'N/m²',
  { showUnitsInScene = true, showUnitInNewLine = false }: LoadSceneLabelFormatOptions = {},
): LoadSceneLabel => {
  if (!showUnitsInScene) {
    return formatLoadForceValue(value)
  }

  if (showUnitInNewLine) {
    const { valueText, unitPrefix } = splitLoadForceValueAndPrefix(value)
    return [valueText, `${unitPrefix}${baseUnit}`]
  }

  return `${formatLoadForceValue(value)}${baseUnit}`
}

export const toLoadSceneTextLines = (label: LoadSceneLabel): string[] =>
  Array.isArray(label) ? label : [label]

export const loadSceneLabelsEqual = (a: LoadSceneLabel, b: LoadSceneLabel): boolean => {
  const normalize = (label: LoadSceneLabel) => (Array.isArray(label) ? label.join('\n') : label)
  return normalize(a) === normalize(b)
}

export const lineLoadForceFormatter = (
  value: number,
  options: LoadSceneLabelFormatOptions = {},
): LoadSceneLabel => formatLoadForceSceneLabel(value, 'N/m', options)

export const scalePointLoadForce = (scale: number, magnitudeScaled: number) => {
  const scaledForce = scale < 1.0e-3 ? 0 : magnitudeScaled * scale
  return scaledForce
}

/** Formats force in Newton as kN numeric value (optional decimals). */
export const formatPointLoadForceKnValue = (forceNewton: number): string =>
  numeral(forceNewton / 1000).format('0.[00]')

/** Formats force in Newton as kN with unit suffix. */
export const formatPointLoadForceKn = (forceNewton: number): string =>
  `${formatPointLoadForceKnValue(forceNewton)} kN`

export const pointLoadForceFormatter = (
  value: number,
  options: LoadSceneLabelFormatOptions = {},
): LoadSceneLabel => formatLoadForceSceneLabel(value, 'N', options)

export const areaLoadForceFormatter = (
  value: number,
  options: LoadSceneLabelFormatOptions = {},
): LoadSceneLabel => formatLoadForceSceneLabel(value, 'N/m²', options)

export const createLineLoadDisplayData = ({
  domain,
  load,
  domainStart,
  domainEnd,
  scale,
  labelFormat,
}: LineLoadDisplayInputData) => {
  const { relative_end, relative_start, start, end } = load

  const loadStartPoint = loadPointOnDomain(relative_start, domainStart, domainEnd)
  const loadEndPoint = loadPointOnDomain(relative_end, domainStart, domainEnd)
  // to global cooredinate
  const forceStart = localToGlobalForce(start, domain).multiplyScalar(-1)
  const forceEnd = localToGlobalForce(end, domain).multiplyScalar(-1)
  // scale load display size
  const scaledForceStart = scaleVector(forceStart, scale)
  const scaledForceEnd = scaleVector(forceEnd, scale)
  // get top ends of the shape
  const loadStartPointPlusForce = loadStartPoint.clone().add(scaledForceStart)
  const loadEndPointPlusForce = loadEndPoint.clone().add(scaledForceEnd)
  // points that will form the shape
  const points = [loadStartPoint, loadEndPoint, loadEndPointPlusForce, loadStartPointPlusForce]
  // for the directional arrow display
  const loadStartMagnitudePoint = magnitudeArrowDirection(start, points[0], points[3])
  const loadEndMagnitudePoint = magnitudeArrowDirection(start, points[1], points[2])

  return {
    loadType: 'line-load',
    label: lineLoadForceFormatter(scaleLineLoadForce(scale, scaledForceStart.z), labelFormat),
    endLabel: lineLoadForceFormatter(scaleLineLoadForce(scale, scaledForceEnd.z), labelFormat),
    magnitudePoints: [loadStartMagnitudePoint, loadEndMagnitudePoint],
    shapeObject: {
      shape: {
        points: points.map(p => toImmutable(p)),
      },
      openings: [],
      domains: [],
      thickness: 0.05,
      storey: '',
    },
  }
}

export const createPointLoadDisplayData = ({
  load,
  domainStart,
  domainEnd,
  scale,
  domain,
  labelFormat,
}: PointLoadDisplayInputData) => {
  const { relative_position, force } = load

  const loadPoint = loadPointOnDomain(relative_position, domainStart, domainEnd)
  const globalForce = localToGlobalForce(force, domain).multiplyScalar(-1) // converForceFromLocalToGlobal(force, selectedDomain).multiplyScalar(-1)
  const scaledGlobalForce = scaleVector(globalForce, scale)
  const loadPointPlusForce = loadPoint.clone().add(scaledGlobalForce)
  const loadMagnitudePoint = magnitudeArrowDirection(force, loadPoint, loadPointPlusForce)

  return {
    loadType: 'point-load',
    label: pointLoadForceFormatter(scalePointLoadForce(scale, scaledGlobalForce.z), labelFormat),
    magnitudePoints: [loadMagnitudePoint],
  }
}
